import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import List from "../../../common/list";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import TextInput from "../../../common/fields/TextInput";
import { useListSearch } from "../../../common/hooks";
import * as UserUploadConstants from "../../../../constants/UserUploadConstants";
import {
  formatUploadTime,
  isFinalStatus,
  resultsFileName,
  statusLabelId,
  statusSeverity,
} from "../../../../helpers/UserUploadHelper";
import {
  userUploadDetailsRequest,
  userUploadDownloadRequest,
  userUploadListRequest,
  userUploadSetQuery,
} from "../../../../store/slices/userUploadSlice";
import UserUploadResult from "./UserUploadResult";

// Administration > Users > Upload history: every employee file uploaded,
// newest first (GET api/users/uploads), with View and the two downloads in
// the row menu. The search (file name or uploader) is sent as namePattern.
export const UserUploadHistory = ({
  labels = [],
  uploads = [],
  totalCount = 0,
  query = { pageNo: 1, pageSize: 20 },
  requesting = false,
  action = null,
  error = null,
  record = null,
  loadUploads,
  setQuery,
  loadUpload,
  downloadFile,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const message = (id) => intl.formatMessage({ id });
  const [viewedId, setViewedId] = useState(null);
  const [namePattern, setNamePattern] = useListSearch(
    query.namePattern,
    (value) => setQuery({ namePattern: value, pageNo: 1 })
  );

  const fetchList = useCallback(() => {
    loadUploads({
      pageNo: query.pageNo,
      pageSize: query.pageSize,
      namePattern: query.namePattern || "",
    });
  }, [query.pageNo, query.pageSize, query.namePattern, loadUploads]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handlePageChange = (pageData) => {
    if (
      query.pageSize !== pageData.rows ||
      query.pageNo !== pageData.page + 1
    ) {
      setQuery({ pageNo: pageData.page + 1, pageSize: pageData.rows });
    }
  };

  const openUpload = (upload) => {
    setViewedId(upload.id);
    loadUpload({ id: upload.id });
  };

  const downloadOriginal = (upload) =>
    downloadFile({
      id: upload.id,
      kind: UserUploadConstants.USER_UPLOAD_FILE_ORIGINAL,
      fileName: upload.fileName,
    });

  const downloadResults = (upload) =>
    downloadFile({
      id: upload.id,
      kind: UserUploadConstants.USER_UPLOAD_FILE_RESULTS,
      fileName: resultsFileName(upload.fileName),
    });

  // Why Download results cannot be used yet (an i18n id).
  const resultsUnavailableReason = (upload) => {
    if (upload.filesExpired) {
      return "user_upload_files_expired_message";
    }
    return isFinalStatus(upload.status)
      ? "user_upload_results_none"
      : "user_upload_results_pending";
  };

  // The row menu (common/moreActions). Everyone who can open this page may
  // use every action; a download that is not available is greyed with the
  // reason.
  const rowActions = [
    {
      label: "user_upload_view",
      icon: "pi-eye",
      isAllowed: true,
      action: openUpload,
    },
    {
      label: "user_upload_download_original",
      icon: "pi-file",
      isAllowed: true,
      additionalCheckForAllowed: (upload) => !upload.filesExpired,
      additionalCheckNotAllowedMessage: "user_upload_files_expired_message",
      action: downloadOriginal,
    },
    {
      label: "user_upload_download_results",
      icon: "pi-download",
      isAllowed: true,
      additionalCheckForAllowed: (upload) =>
        !upload.filesExpired && Boolean(upload.hasResultsFile),
      additionalCheckNotAllowedMessage: resultsUnavailableReason,
      action: downloadResults,
    },
  ];

  const renderStatus = (upload) => (
    <div className="flex flex-column gap-1 align-items-start">
      <Tag
        value={message(statusLabelId(upload.status))}
        severity={statusSeverity(upload.status)}
      />
      {(upload.failureReason && <small>{upload.failureReason}</small>) || null}
      {(upload.filesExpired && (
        <small className="text-secondary">
          {message("user_upload_files_expired")}
        </small>
      )) ||
        null}
    </div>
  );

  const viewed = uploads.find((upload) => upload.id === viewedId);
  const shown = record?.id === viewedId ? record : null;
  const detailsError = !shown && action === "details" && error ? error : null;

  const renderDetails = () => {
    if (shown) {
      return (
        <UserUploadResult upload={shown} onDownloadResults={downloadResults} />
      );
    }
    if (detailsError) {
      return (
        <Message
          severity="error"
          text={message("user_upload_details_failed")}
        />
      );
    }
    return <ProgressBar mode="indeterminate" style={{ height: "6px" }} />;
  };

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {message("user_upload_history")}
          </div>
          <div className="edexlms-page-actions flex gap-2">
            <Button
              type="button"
              icon="pi pi-refresh"
              size="small"
              outlined
              label={message("user_upload_refresh")}
              onClick={fetchList}
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={message("search_user_uploads")}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
          </div>
          <List
            loading={action === "list" && requesting}
            data={uploads}
            totalRecords={totalCount}
            pageQuery={{
              ...query,
              sortColumn: "uploadedAtUtc",
              sortBy: "desc",
            }}
            handlePageChange={handlePageChange}
            handleRowClick={(event) => openUpload(event.data)}
            selectionMode=""
            columns={[
              { field: "fileName", header: "user_upload_column_file_name" },
              {
                field: "uploadedByName",
                header: "user_upload_column_uploaded_by",
                body: (upload) => upload.uploadedByName || "-",
              },
              {
                field: "uploadedAtUtc",
                header: "user_upload_column_uploaded_at",
                body: (upload) => formatUploadTime(upload.uploadedAtUtc),
              },
              {
                field: "status",
                header: "user_upload_column_status",
                body: renderStatus,
              },
              { field: "totalRows", header: "user_upload_column_total" },
              { field: "addedCount", header: "user_upload_result_added" },
              { field: "updatedCount", header: "user_upload_result_updated" },
              { field: "skippedCount", header: "user_upload_result_skipped" },
              { field: "failedCount", header: "user_upload_result_failed" },
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions(rowActions),
              },
            ]}
          />
        </div>
      </div>
      <Dialog
        visible={viewedId !== null}
        onHide={() => setViewedId(null)}
        header={intl.formatMessage(
          { id: "user_upload_details_title" },
          { fileName: viewed?.fileName || shown?.fileName || "" }
        )}
        style={{ width: "60rem" }}
        breakpoints={{ "960px": "95vw" }}
      >
        {renderDetails()}
      </Dialog>
    </div>
  );
};

UserUploadHistory.propTypes = {
  labels: PropTypes.array,
  uploads: PropTypes.array,
  totalCount: PropTypes.number,
  query: PropTypes.object,
  requesting: PropTypes.bool,
  action: PropTypes.string,
  error: PropTypes.object,
  record: PropTypes.object,
  loadUploads: PropTypes.func.isRequired,
  setQuery: PropTypes.func.isRequired,
  loadUpload: PropTypes.func.isRequired,
  downloadFile: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  uploads: state.userUploads.list,
  totalCount: state.userUploads.totalCount,
  query: state.userUploads.query,
  requesting: state.userUploads.requesting,
  action: state.userUploads.action,
  error: state.userUploads.error,
  record: state.userUploads.record,
});

const mapDispatchToProps = (dispatch) => ({
  loadUploads: (query) => dispatch(userUploadListRequest(query)),
  setQuery: (query) => dispatch(userUploadSetQuery(query)),
  loadUpload: (data) => dispatch(userUploadDetailsRequest(data)),
  downloadFile: (data) => dispatch(userUploadDownloadRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserUploadHistory);
